import { useEffect, useRef, useState } from 'react'
import { QRCode } from 'react-qrcode-logo'
import {
  AbsoluteFill,
  Easing,
  continueRender,
  delayRender,
  interpolate,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion'
import ZealousSvg from '../assets/ZealousSvg'
import {
  ResponsiveSceneProvider,
  useResponsiveSceneProps,
} from '../components/ResponsiveSceneContext'
import { SceneProps } from '../types'

export function QrScene({ audiogram, orientation, scene }: SceneProps) {
  return (
    <ResponsiveSceneProvider
      audiogram={audiogram}
      scene={scene}
      orientation={orientation}
    >
      <Background />
      <TopStripe />
      <BottomStripe />

      <QrCode />

      <TextSection />
    </ResponsiveSceneProvider>
  )
}

const Background = () => {
  const { audiogram } = useResponsiveSceneProps()

  return (
    <AbsoluteFill style={{ backgroundColor: audiogram.theme.colors.primary }} />
  )
}

const QrCode = () => {
  const { audiogram, orientation, u, w } = useResponsiveSceneProps()

  const placement = {
    landscape: { top: u(5), bottom: u(5), left: u(3), right: u(40) },
    square: { top: u(4), bottom: u(19), left: u(2), right: u(2) },
    portrait: { top: u(6), bottom: u(35), left: u(2), right: u(2) },
  }[orientation]

  return (
    <div
      className="absolute flex items-center justify-center"
      style={{
        ...placement,
      }}
    >
      <div className="flex aspect-square items-center justify-center">
        <div className="flex aspect-square items-center justify-center p-10">
          <QRCode
            bgColor={audiogram.theme.colors.primary}
            fgColor={audiogram.theme.colors.secondary}
            size={orientation === 'square' ? 320 : 600}
            value={audiogram.episode.url || 'https://zealous.one'}
          />
        </div>
      </div>
      <StarA />
      <StarB />
    </div>
  )
}

const Logo = () => {
  const { u, audiogram, orientation } = useResponsiveSceneProps()

  const bottom = {
    landscape: u(8),
    square: u(1),
    portrait: u(8),
  }[orientation]

  const width = {
    landscape: u(10),
    square: u(9),
    portrait: u(10),
  }[orientation]

  return (
    <ZealousSvg
      className="absolute"
      style={{
        bottom,
        left: '50%',
        width,
        transform: 'translate(-50%,100%)',
      }}
      color={audiogram.theme.colors.secondary}
    />
  )
}

const PoweredBy = () => {
  const { audiogram, u, w, h, orientation } = useResponsiveSceneProps()

  const bottom = {
    landscape: u(9),
    square: u(2),
    portrait: u(9),
  }[orientation]

  return (
    <div
      className="absolute"
      style={{
        color: audiogram.theme.colors.secondary,
        opacity: 0.5,
        textAlign: 'center',
        fontSize: u(1.25),
        fontFamily: audiogram.theme.fonts.primary,
        left: 0,
        right: 0,
        bottom,
      }}
    >
      powered by
    </div>
  )
}

const StarA = () => {
  const { audiogram, u, w, h } = useResponsiveSceneProps()
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const pathRef = useRef<SVGPathElement>(null)
  const [handle] = useState(() => delayRender())

  useEffect(() => {
    // render once the ref is available
    continueRender(handle)
  }, [handle])

  const time = frame / fps

  const rate = 0.25 * 360 * time

  const enterDriver = interpolate(time, [0, 1], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.out(Easing.exp),
  })

  const x = interpolate(enterDriver, [0, 0.5, 1], [0, 0, 100], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })
  const y = interpolate(enterDriver, [0, 0.5, 1], [100, 0, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })

  const pathLength = pathRef?.current?.getTotalLength() ?? 0

  return (
    <>
      <svg
        className="absolute overflow-visible"
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        fill="none"
      >
        <path
          ref={pathRef}
          d="M 0 100 L 0 0 L 100 0"
          strokeWidth={u(0.025)}
          stroke={audiogram.theme.colors.secondary}
          strokeDasharray={pathLength}
          strokeDashoffset={pathLength * (1 - enterDriver)}
        />
        <g
          transform={`translate(${x}, ${y}) rotate(${-rate}) scale(0.3) translate(-22,-22) `}
        >
          <path
            d="M-1.909e-06 22.2014C19.5136 20.9333 20.5304 19.9164 21.7986 0.402826C23.0667 19.9164 24.0835 20.9333 43.5972 22.2014C24.0835 23.4696 23.0667 24.4864 21.7986 44C20.5304 24.4864 19.5136 23.4696 -1.909e-06 22.2014Z"
            fill={audiogram.theme.colors.secondary}
          />
        </g>
      </svg>
    </>
  )
}

const StarB = () => {
  const { audiogram, u, w, h } = useResponsiveSceneProps()
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const pathRef = useRef<SVGPathElement>(null)
  const [handle] = useState(() => delayRender())

  useEffect(() => {
    // render once to the ref is available
    continueRender(handle)
  }, [handle])

  const time = frame / fps

  const rate = 0.25 * 360 * time

  const enterDriver = interpolate(time, [0, 1], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.out(Easing.exp),
  })

  const x = interpolate(enterDriver, [0, 0.5, 1], [100, 100, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })
  const y = interpolate(enterDriver, [0, 0.5, 1], [0, 100, 100], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })

  const pathLength = pathRef?.current?.getTotalLength() ?? 0

  return (
    <>
      <svg
        className="absolute overflow-visible"
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        fill="none"
      >
        <path
          ref={pathRef}
          d="M 100 0 L 100 100 L 0 100"
          strokeWidth={u(0.025)}
          stroke={audiogram.theme.colors.secondary}
          strokeDasharray={pathLength}
          strokeDashoffset={pathLength * (1 - enterDriver)}
        />
        <g
          transform={`translate(${x}, ${y}) rotate(${-rate}) scale(0.3) translate(-22,-22) `}
        >
          <path
            d="M-1.909e-06 22.2014C19.5136 20.9333 20.5304 19.9164 21.7986 0.402826C23.0667 19.9164 24.0835 20.9333 43.5972 22.2014C24.0835 23.4696 23.0667 24.4864 21.7986 44C20.5304 24.4864 19.5136 23.4696 -1.909e-06 22.2014Z"
            fill={audiogram.theme.colors.secondary}
          />
        </g>
      </svg>
    </>
  )
}

const Callout = () => {
  const { audiogram, u, w, h } = useResponsiveSceneProps()

  return (
    <div
      className="absolute flex flex-col items-center justify-start overflow-hidden text-7xl"
      style={{
        color: audiogram.theme.colors.secondary,
        right: 0,
        height: u(8),
        left: 0,
        top: 0,
        fontFamily: audiogram.theme.fonts.primary,
        lineHeight: 1.2,
      }}
    >
      Listen now
    </div>
  )
}

const TopStripe = () => {
  const { audiogram, u, w } = useResponsiveSceneProps()
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const time = frame / fps
  const scale = interpolate(time - 0.1, [0, 1], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.out(Easing.exp),
  })

  return (
    <div
      className="absolute"
      style={{
        backgroundColor: audiogram.theme.colors.accent,
        top: 0,
        height: u(2),
        left: 0,
        width: w(),
        transform: `translate(-50%,50%) scaleX(${scale}) translate(50%,-50%)`,
        opacity: scale,
      }}
    />
  )
}

const BottomStripe = () => {
  const { audiogram, u, w } = useResponsiveSceneProps()
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const time = frame / fps
  const scale = interpolate(time - 0.1, [0, 1], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.out(Easing.exp),
  })

  return (
    <div
      className="absolute"
      style={{
        backgroundColor: audiogram.theme.colors.accent,
        bottom: 0,
        height: u(2),
        right: 0,
        width: w(),
        transform: `translate(50%,50%) scaleX(${scale}) translate(-50%,-50%)`,
        opacity: scale,
      }}
    />
  )
}

const TextSection = () => {
  const { orientation, u, w } = useResponsiveSceneProps()

  const left = {
    landscape: w(0.45),
    square: u(2),
    portrait: u(2),
  }[orientation]

  const height = {
    landscape: u(24),
    square: u(12),
    portrait: u(28),
  }[orientation]

  return (
    <div
      className="absolute"
      style={{
        right: u(2),
        bottom: u(5),
        left,
        height,
      }}
    >
      <Callout />

      <PoweredBy />
      <Logo />
    </div>
  )
}
